import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  MenuItem,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import type { PatientIntakeFormState } from '../../../constants/patient';
import {
  careInsuranceLevelOptions,
  careInsuranceStatusOptions,
  pharmacyStatusOptions,
  yesNoOptions,
} from '../../../constants/patient';
import IntakeFormRow from '../IntakeFormRow';
import IntakeTextField from '../IntakeTextField';
import IntakeDatePicker from '../IntakeDatePicker';
import IntakeToggleGroup from '../IntakeToggleGroup';

type Props = {
  form: PatientIntakeFormState;
  patchForm: (p: Partial<PatientIntakeFormState>) => void;
};

const accordionSx = { border: 1, borderColor: 'divider', mt: 1, '&:before': { display: 'none' } };

const CareServiceFields: React.FC<Props> = ({ form, patchForm }) => {
  const setVisitNursing = (partial: Partial<PatientIntakeFormState['visitNursing']>) => {
    patchForm({ visitNursing: { ...form.visitNursing, ...partial } });
  };

  const setOtherCare = (partial: Partial<PatientIntakeFormState['otherCareServices']>) => {
    patchForm({ otherCareServices: { ...form.otherCareServices, ...partial } });
  };

  const serviceRow = (
    label: string,
    key: 'visit_rehab' | 'day_service' | 'helper' | 'other',
    noteKey: 'visit_rehab_note' | 'day_service_note' | 'helper_note' | 'other_note'
  ) => (
    <IntakeFormRow key={key} label={label} align="flex-start">
      <div>
        <IntakeToggleGroup
          options={yesNoOptions}
          value={form.otherCareServices[key] ?? ''}
          onChange={(v) => setOtherCare({ [key]: v as 'yes' | 'no' })}
          ariaLabel={label}
        />
        <IntakeTextField
          placeholder="備考"
          value={form.otherCareServices[noteKey] ?? ''}
          onChange={(e) => setOtherCare({ [noteKey]: e.target.value })}
          sx={{ mt: 0.5 }}
        />
      </div>
    </IntakeFormRow>
  );

  return (
    <>
      <IntakeFormRow label="介護保険">
        <IntakeTextField
          select
          value={form.careInsuranceStatus}
          onChange={(e) =>
            patchForm({
              careInsuranceStatus: e.target.value as PatientIntakeFormState['careInsuranceStatus'],
              careInsuranceLevel: '',
            })
          }
        >
          <MenuItem value="">未選択</MenuItem>
          {careInsuranceStatusOptions.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </IntakeTextField>
      </IntakeFormRow>

      {form.careInsuranceStatus === 'certified' && (
        <IntakeFormRow label="介護度">
          <IntakeTextField
            select
            value={form.careInsuranceLevel}
            onChange={(e) =>
              patchForm({
                careInsuranceLevel: e.target.value as PatientIntakeFormState['careInsuranceLevel'],
              })
            }
          >
            <MenuItem value="">未選択</MenuItem>
            {careInsuranceLevelOptions.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </IntakeTextField>
        </IntakeFormRow>
      )}

      <IntakeFormRow label="居宅介入">
        <IntakeToggleGroup
          options={yesNoOptions}
          value={form.homeCareIntervention === null ? '' : form.homeCareIntervention ? 'yes' : 'no'}
          onChange={(v) =>
            patchForm({
              homeCareIntervention: v === 'yes',
              homeCareContactName: v === 'yes' ? form.homeCareContactName : '',
            })
          }
          ariaLabel="居宅介入"
        />
      </IntakeFormRow>

      {form.homeCareIntervention === true && (
        <IntakeFormRow label="担当者名">
          <IntakeTextField
            value={form.homeCareContactName}
            onChange={(e) => patchForm({ homeCareContactName: e.target.value })}
            placeholder="ケアマネさんの名前など"
          />
        </IntakeFormRow>
      )}

      <Accordion
        defaultExpanded={form.visitNursing.has_service === true}
        disableGutters
        elevation={0}
        sx={accordionSx}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">訪問看護（詳細）</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ bgcolor: 'background.paper', pt: 0.5 }}>
          <IntakeFormRow label="訪問看護">
            <IntakeToggleGroup
              options={yesNoOptions}
              value={
                form.visitNursing.has_service === undefined
                  ? ''
                  : form.visitNursing.has_service
                    ? 'yes'
                    : 'no'
              }
              onChange={(v) => setVisitNursing({ has_service: v === 'yes' })}
              ariaLabel="訪問看護の有無"
            />
          </IntakeFormRow>

          {form.visitNursing.has_service === true && (
            <>
              <IntakeFormRow label="事業所名">
                <IntakeTextField
                  value={form.visitNursing.office_name ?? ''}
                  onChange={(e) => setVisitNursing({ office_name: e.target.value })}
                />
              </IntakeFormRow>
              <IntakeFormRow label="介入頻度">
                <IntakeTextField
                  value={form.visitNursing.frequency ?? ''}
                  onChange={(e) => setVisitNursing({ frequency: e.target.value })}
                />
              </IntakeFormRow>
              <IntakeFormRow label="指示書発行元">
                <IntakeTextField
                  value={form.visitNursing.instruction_source ?? ''}
                  onChange={(e) => setVisitNursing({ instruction_source: e.target.value })}
                />
              </IntakeFormRow>
              <IntakeFormRow label="期間（開始）">
                <IntakeDatePicker
                  value={form.visitNursing.period_start ?? ''}
                  onChange={(v) => setVisitNursing({ period_start: v || undefined })}
                />
              </IntakeFormRow>
              <IntakeFormRow label="期間（終了）">
                <IntakeDatePicker
                  value={form.visitNursing.period_end ?? ''}
                  onChange={(v) => setVisitNursing({ period_end: v || undefined })}
                />
              </IntakeFormRow>
            </>
          )}
        </AccordionDetails>
      </Accordion>

      <Accordion
        defaultExpanded={Object.keys(form.otherCareServices).length > 0}
        disableGutters
        elevation={0}
        sx={accordionSx}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">その他介護（詳細）</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ bgcolor: 'background.paper', pt: 0.5 }}>
          {serviceRow('訪問リハ', 'visit_rehab', 'visit_rehab_note')}
          {serviceRow('デイサービス', 'day_service', 'day_service_note')}
          {serviceRow('ヘルパー', 'helper', 'helper_note')}
          {serviceRow('その他', 'other', 'other_note')}
          <IntakeFormRow label="備考" align="flex-start">
            <IntakeTextField
              multiline
              minRows={2}
              value={form.otherCareServices.general_note ?? ''}
              onChange={(e) => setOtherCare({ general_note: e.target.value })}
            />
          </IntakeFormRow>
        </AccordionDetails>
      </Accordion>

      <Accordion
        defaultExpanded={
          form.pharmacyStatus === 'has' ||
          Boolean(form.pharmacyNote?.trim()) ||
          Boolean(form.pharmacyName?.trim())
        }
        disableGutters
        elevation={0}
        sx={accordionSx}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">かかりつけ薬局（詳細）</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ bgcolor: 'background.paper', pt: 0.5 }}>
          <IntakeFormRow label="かかりつけ薬局">
            <IntakeTextField
              select
              value={form.pharmacyStatus}
              onChange={(e) =>
                patchForm({
                  pharmacyStatus: e.target.value as PatientIntakeFormState['pharmacyStatus'],
                  pharmacyName: e.target.value === 'has' ? form.pharmacyName : '',
                })
              }
            >
              <MenuItem value="">未選択</MenuItem>
              {pharmacyStatusOptions.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </IntakeTextField>
          </IntakeFormRow>

          {form.pharmacyStatus === 'has' && (
            <IntakeFormRow label="薬局名">
              <IntakeTextField
                value={form.pharmacyName}
                onChange={(e) => patchForm({ pharmacyName: e.target.value })}
              />
            </IntakeFormRow>
          )}

          <IntakeFormRow label="備考" align="flex-start">
            <IntakeTextField
              multiline
              minRows={2}
              placeholder="訪薬に関する補足など"
              value={form.pharmacyNote}
              onChange={(e) => patchForm({ pharmacyNote: e.target.value })}
            />
          </IntakeFormRow>
        </AccordionDetails>
      </Accordion>
    </>
  );
};

export default CareServiceFields;
